import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import AddContactDetails from "../../PoaSteps/addContactDetails";
import POAInfo from "../../PoaSteps/poaInfo";
import POADuration from "../../PoaSteps/poaDuration";
import POASign from "../../PoaSteps/poaSign";
import POABankID from "../../PoaSteps/poaBankID";
import {
  ENUMPOASteps,
  usePOAStepsStore,
} from "../../../hooks/ui/poaStepsDataStore";
import {
  isFullCredentials,
  useUpdateCustomerData,
} from "../../../hooks/ui/customerDataStore";
import { getFetchPOAText } from "../../../hooks/api/useFetchPOAText";
import { AppWebviewLayout } from "../AppWebviewLayout";
import { IntroScreen, type IntroStep } from "../IntroScreen";
import { PoaDataRetrievalScreen } from "./PoaDataRetrievalScreen";
import { AppPensionOverview } from "../overview/AppPensionOverview";
import reloadIconUrl from "../../../assets/reload-icon.svg?url";
import walletIconUrl from "../../../assets/wallet-icon.svg?url";
import diagramIconUrl from "../../../assets/diagram-icon.svg?url";
import { APP_POA_STEP, trackEvent } from "../../../utils/statsig";

const INTRO_STEPS = [
  {
    icon: reloadIconUrl,
    titleId: "app.poaOnb.start.benefit.title.1",
    title: "Se var dina pensioner finns",
    descriptionId: "app.poaOnb.start.benefit.description.1",
    description:
      "Vi hjälper dig samla informationen på ett ställe så att du får en överblick över ditt totala pensionskapital.",
  },
  {
    icon: walletIconUrl,
    titleId: "app.poaOnb.start.benefit.title.2",
    title: "Få en prognos för framtiden",
    descriptionId: "app.poaOnb.start.benefit.description.2",
    description:
      "Se en uppskattning av vad du kan få i pension baserat på ditt nuvarande sparande.",
  },
  {
    icon: diagramIconUrl,
    titleId: "app.poaOnb.start.benefit.title.3",
    title: "Förstå vad pensionen består av",
    descriptionId: "app.poaOnb.start.benefit.description.3",
    description: "Få insikter kring hur dina pensionstillgångar är uppdelade.",
  },
];

type Phase = "intro" | "dataRetrieval" | "poa";

const APP_STEPS = [
  ENUMPOASteps.EMAILANDPHONE,
  ENUMPOASteps.POAINFO,
  ENUMPOASteps.POADURATION,
  ENUMPOASteps.POACONFIRMATION,
  ENUMPOASteps.POASIGN,
  ENUMPOASteps.CONFIRMATION,
];

const FUNNEL_STEP: Partial<Record<ENUMPOASteps, string>> = {
  [ENUMPOASteps.EMAILANDPHONE]: APP_POA_STEP.contactDetails,
  [ENUMPOASteps.POAINFO]: APP_POA_STEP.poaInfo,
  [ENUMPOASteps.POADURATION]: APP_POA_STEP.duration,
  [ENUMPOASteps.POACONFIRMATION]: APP_POA_STEP.sign,
};

// The step store is a sessionStorage singleton shared with /signup and
// /poa-renew, so only a run this flow left behind is ours to resume.
const resumableStep = () => {
  const { app, currentStep } = usePOAStepsStore.getState();
  return app && APP_STEPS.includes(currentStep) ? currentStep : null;
};

export interface AppPoaFlowProps {
  prefillData: { email?: string; phoneNumber?: string } | null;
  // Only the app knows whether its build has the Sam chat.
  isChatAvailable?: boolean;
  /**
   * A User node exists in Neptune. That is enough to open the overview: a POA that has
   * not produced data yet is explained by the banner on the overview itself.
   */
  isCustomer?: boolean;
}

export function AppPoaFlow({
  prefillData,
  isChatAvailable,
  isCustomer = false,
}: AppPoaFlowProps) {
  const intl = useIntl();
  // BankID sends the user back to this page mid-flow.
  const [phase, setPhase] = useState<Phase>(() =>
    resumableStep() ? "poa" : "intro",
  );
  // A customer whose POA ended signs a new one. Fullmaktskollen has no re-enable, so
  // this is the renewal path web uses: no contact details, straight to the POA info.
  // Read back from the persisted store so a BankID return lands in the flow again.
  const [reactivating, setReactivating] = useState(
    () => resumableStep() !== null && usePOAStepsStore.getState().renewal,
  );
  const { currentStep, setStep, setApp, setRenewal } = usePOAStepsStore();
  const { updateEmail, updatePhoneNumber, customerData } =
    useUpdateCustomerData();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!resumableStep()) setStep(ENUMPOASteps.EMAILANDPHONE);
    setApp(true);
  }, [setApp, setStep]);

  useEffect(() => {
    if (prefillData?.email) updateEmail(prefillData.email);
    if (prefillData?.phoneNumber) updatePhoneNumber(prefillData.phoneNumber);
  }, [prefillData, updateEmail, updatePhoneNumber]);

  const { credentials } = customerData;
  useEffect(() => {
    if (!isFullCredentials(credentials)) return;
    // POASign blocks its button on this text, so warm it four screens early.
    // prefetchQuery swallows failures, leaving the retry to POASign.
    queryClient.prefetchQuery({
      queryKey: ["fetch-poa-text"],
      queryFn: () => getFetchPOAText({ credentials }),
    });
  }, [queryClient, credentials]);

  // Known to us already, so the intro and the signing steps have nothing to add.
  // isCustomer covers someone coming back, CONFIRMATION the one who just finished,
  // including a reactivation.
  const showOverview =
    currentStep === ENUMPOASteps.CONFIRMATION || (isCustomer && !reactivating);

  const viewedStep = showOverview
    ? undefined
    : phase === "intro"
      ? APP_POA_STEP.intro
      : phase === "dataRetrieval"
        ? APP_POA_STEP.dataRetrieval
        : FUNNEL_STEP[currentStep];

  useEffect(() => {
    if (viewedStep) {
      trackEvent("pension_app_poa_step_viewed", { step: viewedStep });
    }
  }, [viewedStep]);

  const startReactivation = () => {
    setRenewal(true);
    setStep(ENUMPOASteps.POAINFO);
    setPhase("dataRetrieval");
    setReactivating(true);
  };

  if (showOverview) {
    return (
      <AppPensionOverview
        isChatAvailable={isChatAvailable}
        onReactivate={startReactivation}
      />
    );
  }

  if (phase === "intro") {
    return (
      <IntroScreen
        title={intl.formatMessage({
          id: "app.poaOnb.intro.headline",
          defaultMessage: "Pensionsöversikt",
        })}
        intro={intl.formatMessage({
          id: "app.poaOnb.intro.description",
          defaultMessage:
            "Se det viktigaste om din pension samlat på ett ställe, och få bättre koll på ditt sparande.",
        })}
        steps={INTRO_STEPS.map(
          ({
            icon,
            titleId,
            title,
            descriptionId,
            description,
          }): IntroStep => ({
            icon,
            title: intl.formatMessage({ id: titleId, defaultMessage: title }),
            description: intl.formatMessage({
              id: descriptionId,
              defaultMessage: description,
            }),
          }),
        )}
        ctaLabel={intl.formatMessage({
          id: "bottomNav.next",
          defaultMessage: "Nästa",
        })}
        ctaProps={{ rightSection: false, fz: "md" }}
        onContinue={() => {
          trackEvent("pension_app_poa_next_clicked", {
            step: APP_POA_STEP.intro,
          });
          setPhase("dataRetrieval");
        }}
      />
    );
  }

  if (phase === "dataRetrieval") {
    return (
      <PoaDataRetrievalScreen
        onBack={() => {
          trackEvent("pension_app_poa_back_clicked", {
            step: APP_POA_STEP.dataRetrieval,
          });
          // A reactivation started from the overview, so back is the overview.
          if (reactivating) {
            setRenewal(false);
            setReactivating(false);
          } else {
            setPhase("intro");
          }
        }}
        onContinue={() => {
          trackEvent("pension_app_poa_next_clicked", {
            step: APP_POA_STEP.dataRetrieval,
          });
          setPhase("poa");
        }}
      />
    );
  }

  return (
    <AppWebviewLayout footerSlot scrollResetKey={currentStep}>
      {renderStep(currentStep, () => setPhase("dataRetrieval"), reactivating)}
    </AppWebviewLayout>
  );
}

function renderStep(
  step: ENUMPOASteps,
  toDataRetrieval: () => void,
  reactivating: boolean,
) {
  switch (step) {
    case ENUMPOASteps.EMAILANDPHONE:
      return <AddContactDetails onBack={toDataRetrieval} />;
    case ENUMPOASteps.POAINFO:
      // A reactivation skips the contact details.
      return <POAInfo onBack={reactivating ? toDataRetrieval : undefined} />;
    case ENUMPOASteps.POADURATION:
      return <POADuration />;
    case ENUMPOASteps.POACONFIRMATION:
      return <POASign />;
    case ENUMPOASteps.POASIGN:
      return <POABankID />;
    default:
      return null;
  }
}
